import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Zap } from 'lucide-react';

import useAssignmentRuleApi, { rowToFormValues } from './config';
import { evaluateAssignmentRules } from '../../services/trainingService';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';
import Button from '../../components/ui/Button';

const Container = styled.div``;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
`;

const Summary = styled.div`
  background: #F0FDF4;
  border: 1px solid #86EFAC;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 16px;
  font-size: 13px;
  color: #166534;
  white-space: pre-line;
`;

export default function TrainingAssignmentRulesPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, ids: [], name: '' });
  const [deleting, setDeleting] = useState(false);

  const [evalRunning, setEvalRunning] = useState(false);
  const [evalResult, setEvalResult] = useState(null);

  const { objectSchema, fetching, columns: baseColumns, data, pagination, create, update, _delete }
    = useAssignmentRuleApi(queryParams);

  const handleNewButtonClick = useCallback(() => {
    setSelectedRow(null);
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setSelectedRow(rowToFormValues(row));
    setDrawerOpen(true);
  }, []);

  const handleSave = useCallback(async (formData) => {
    const success = selectedRow?.id
      ? await update(selectedRow.id, formData)
      : await create(formData);
    if (success) setDrawerOpen(false);
    return success;
  }, [selectedRow, create, update]);

  const handleDelete = useCallback((row) => {
    setDeleteConfirm({ isOpen: true, ids: [row.id], name: row.name });
  }, []);

  const handleRowsDelete = useCallback((ids) => {
    setDeleteConfirm({ isOpen: true, ids, name: `${ids.length} ${t('assignmentRule.name').toLowerCase()}` });
  }, [t]);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    await _delete(deleteConfirm.ids);
    setDeleteConfirm({ isOpen: false, ids: [], name: '' });
    setDeleting(false);
  }, [_delete, deleteConfirm.ids]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((fl) => setFilterList(fl), []);

  const handleEvaluate = useCallback(async () => {
    setEvalRunning(true);
    try {
      const res = await evaluateAssignmentRules();
      setEvalResult(res.data?.data);
      setQueryParams((p) => ({ ...p }));
    } finally { setEvalRunning(false); }
  }, []);

  const actionOptions = useMemo(() => {
    if (!canWrite('TR')) return [];
    return [
      { type: 'edit', label: t('assignmentRule.edit'), onClick: handleEdit },
      { type: 'divider' },
      { type: 'delete', label: t('common.delete'), onClick: handleDelete },
    ];
  }, [t, canWrite, handleEdit, handleDelete]);

  const columns = useMemo(() => {
    if (actionOptions.length === 0) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            return <OptionsMenu actions={actionOptions} row={row} />;
          },
        },
      },
    ];
  }, [baseColumns, actionOptions, data]);

  return (
    <Container>
      {canWrite('TR') && (
        <Actions>
          <Button type="button" $variant="outline" onClick={handleEvaluate} disabled={evalRunning}>
            <Zap size={14} style={{ marginRight: 4 }} />
            {evalRunning ? t('common.loading') : t('assignmentRule.runNow')}
          </Button>
        </Actions>
      )}

      {evalResult && (
        <Summary>
          {t('assignmentRule.evalSummary', {
            rules: evalResult.rulesEvaluated,
            assignments: evalResult.assignmentsCreated,
            users: evalResult.usersAffected,
          })}
          {(evalResult.messages || []).length > 0 && '\n' + evalResult.messages.join('\n')}
        </Summary>
      )}

      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedRow?.id ? t('assignmentRule.edit') : t('assignmentRule.new')}
        schema={objectSchema}
        initialValues={selectedRow}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('assignmentRule.deleteTitle')}
        message={t('assignmentRule.deleteMessage', { name: deleteConfirm.name })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, ids: [], name: '' })}
        loading={deleting}
      />

      {useMemo(() => (
        <DataTable
          title={t('menu.trainingAssignmentRules')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={canWrite('TR')}
          newButtonLabel={t('assignmentRule.new')}
          onNewButtonClick={handleNewButtonClick}
          onRowsDelete={canWrite('TR') ? handleRowsDelete : undefined}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, columns, data, pagination, canWrite,
        handleNewButtonClick, handleRowsDelete, filterList,
        handleFilterConfirm, handleDataStateChange,
      ])}
    </Container>
  );
}
